import React from "react";
import { Star } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const CLAIM_URL = "https://trksy.org/aff_c?offer_id=4685&aff_id=74367";

const steps = [
  { n: 1, text: 'Click "Claim Now"' },
  { n: 2, text: "Enter your email and basic info" },
  { n: 3, text: "Complete 4\u20135 sponsored deals" },
  { n: 4, text: "Enjoy your $750 voucher!" },
];

const faqs = [
  {
    q: "How long do the deals take?",
    a: "Most deals only take a few minutes to complete. Some may involve a free trial or a small sign-up, so the exact time can vary slightly from one deal to the next.",
  },
  {
    q: "What are deals?",
    a: "Deals are sponsored offers from our partners \u2014 such as free trials, app downloads, or simple sign-ups. Completing them is what allows us to provide your voucher at no cost to you.",
  },
  {
    q: "How many deals do I have to do?",
    a: "Typically you will need to complete 4\u20135 sponsored deals in order to fully unlock and verify your voucher.",
  },
  {
    q: "When will I receive my voucher?",
    a: "Once your deals are verified, your Tory Burch voucher code will be delivered to your email within 24\u201348 hours.",
  },
];

const StepCard = ({ n, text }) => (
  <div className="flex items-center gap-4 bg-white rounded-2xl px-5 py-4 shadow-[0_2px_10px_rgba(15,32,56,0.06)] border border-[#f0e7dd]">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#c07a52] text-white flex items-center justify-center text-sm font-semibold">
      {n}
    </div>
    <p className="text-[#1a1a1a] text-[15px] sm:text-base font-medium">{text}</p>
  </div>
);

const TrustpilotStars = () => (
  <div className="flex items-center gap-[3px] bg-[#00b67a] px-1.5 py-1 rounded-[3px]">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} size={16} className="text-white fill-white" />
    ))}
  </div>
);

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#faf5ef] flex flex-col">
      {/* Top banner */}
      <div className="w-full bg-[#f6ddc9] text-center py-2.5 px-4">
        <span className="text-[13px] sm:text-sm font-medium text-[#7a3e12] tracking-wide">
          3,500+ People Already Claimed
        </span>
      </div>

      <div className="flex-1 w-full max-w-[560px] mx-auto px-5 pt-10 pb-16">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="/tory-burch-logo.png"
            alt="Tory Burch"
            className="h-14 sm:h-[60px] w-auto object-contain"
          />
        </div>

        {/* Heading */}
        <h1 className="text-center text-[#3a3a3a] text-[22px] sm:text-[26px] font-normal mb-8">
          Claim your{" "}
          <span className="text-[#c07a52] font-semibold">$750</span> Tory Burch
          voucher
        </h1>

        {/* Steps */}
        <div className="flex flex-col gap-3.5 mb-7">
          {steps.map((s) => (
            <StepCard key={s.n} n={s.n} text={s.text} />
          ))}
        </div>

        {/* CTA */}
        <a
          href={CLAIM_URL}
          className="block w-full text-center bg-[#0f2038] hover:bg-[#16294a] active:scale-[0.99] transition-all duration-200 text-white font-semibold tracking-wide text-[17px] rounded-full py-4 shadow-[0_6px_18px_rgba(15,32,56,0.25)]"
        >
          CLAIM NOW
        </a>
        <p className="text-center text-[12.5px] text-[#9a9a9a] mt-3">
          Completion of sponsored deals is required to claim your voucher.
        </p>

        {/* FAQ */}
        <div className="mt-12">
          <h2 className="text-[#1a1a1a] text-xl sm:text-2xl font-bold mb-4">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="flex flex-col gap-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="bg-white rounded-2xl border border-[#f0e7dd] px-5 shadow-[0_2px_10px_rgba(15,32,56,0.05)]"
              >
                <AccordionTrigger className="text-left text-[15px] sm:text-base font-medium text-[#1a1a1a] hover:no-underline py-4">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[14px] text-[#5a5a5a] leading-relaxed pb-4">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Trustpilot */}
        <div className="mt-10 flex items-center justify-center gap-2.5">
          <span className="text-[#1a1a1a] font-semibold text-[15px]">
            Excellent
          </span>
          <TrustpilotStars />
          <span className="text-[#1a1a1a] font-medium text-[15px]">
            4.9 / 5
          </span>
        </div>
      </div>
    </div>
  );
}
